// "Prepare for a Test" — Phase 8 port of rewise-ui's Phase 5 into
// rewise_std+1 (grade 11-12), reworked to a single search-and-add entry
// point (no subject-first gating): the student searches across their whole
// grade's catalog at once, adds as many topics as they like (typos fall
// back to a suggested match; anything not in the catalog can be added as
// free text and runs through the guided-session engine's existing
// raw-input path), then runs each topic through the guided-session engine,
// auto-advancing to the next item when one concludes.
//
// Phase 10: the post-add experience is a persistent-panel hub
// (`session_hub`), not a linear explaining->guided_practice->practice_choice
// ->practicing pipeline. The student can switch between Explanation / Try
// Together / MCQ / Short Answer / Long Answer / Cheat Sheet at will, at any
// point, via `activeMode` -- accumulated state (explanationBlocks, subTopics,
// chatTurns) persists underneath regardless of which mode is showing.
// Concluding a topic is an explicit student action ("Done with this topic"),
// not something the hard iteration caps force automatically -- they now
// surface as a soft note instead.
//
// Phase 11: two independent axes, not one upfront question. `level`
// (foundation/standard/advanced/competitive -- "at what academic rigor")
// defaults from the student's grade but is never gated by it, and is a
// persistent, always-switchable tab row above the mode panel. `depth`
// (quick_recap/explain_properly/lets_just_solve -- "what do you want to do
// right now") no longer has an upfront gate screen either -- it defaults to
// explain_properly and is reachable via two extra actions inside Explanation
// mode ("just a quick recap" / "skip to practice"). Switching either axis
// regenerates the explanation fresh at the new rigor.
//
// Follows the same local Phase-state-machine idiom as UnderstandSessionPage.tsx
// (not shared — no shared wizard component in this codebase), with a queue
// layered on top instead of a single concept.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Users,
  CircleDot,
  PenLine,
  FileText,
  NotebookText,
  Sprout,
  Target,
  Zap,
  Trophy,
  Globe,
  HelpCircle,
  Repeat,
  Calculator,
  PartyPopper,
  Lightbulb,
  Camera,
  ClipboardPaste,
  NotebookPen,
  ArrowRight,
} from "lucide-react";
import { ModeSelector, type ModeOption } from "@branon-shared/ui/ModeSelector";
import { TopTearCard } from "@branon-shared/ui/TopTearCard";
import { SpillingDoodlesCompact } from "@branon-shared/ui/Doodle";
import { useAuth } from "../hooks/useAuth";
import { fetchConceptsPlus1ByIds, fetchConceptsPlus1 } from "../services/conceptPlus1Service";
import { getStudentConceptState, type StudentConceptState } from "../services/masteryService";
import {
  startGuidedSession,
  explainAtDepth,
  sendGuidedPracticeTurn,
  choosePracticeMode,
  evaluatePracticeAnswer,
  concludeGuidedSession,
  decodeUploadedImage,
  resizeImageFileForUpload,
  defaultLevelForGrade,
  competitiveContextForSubject,
  subjectSupportsCompetitive,
  type Depth,
  type Level,
  type PracticeMode,
  type GuidedConceptContext,
} from "../services/guidedSessionService";
import { generateExamPrepContent, gradeBandFor, type CheatSheetContent } from "../services/examPrepOrchestrationService";
import type { ConceptPlus1 } from "../types";
import MarkdownExplanation from "../components/MarkdownExplanation";
import TTSButton from "../components/TTSButton";

type Phase = "loading" | "pick_topics" | "session_hub" | "concluding" | "queue_complete";

// Which panel mode is currently showing within the hub. Switchable at will
// once the hub is reached -- not a linear sequence.
type ActiveMode = "explanation" | "try_together" | PracticeMode | "cheat_sheet";

const MODE_OPTIONS: Record<ActiveMode, ModeOption<ActiveMode>> = {
  explanation: { value: "explanation", label: "Explanation", icon: BookOpen },
  try_together: { value: "try_together", label: "Try Together", icon: Users },
  mcq: { value: "mcq", label: "MCQ", icon: CircleDot },
  short_answer: { value: "short_answer", label: "Short Answer", icon: PenLine },
  long_answer: { value: "long_answer", label: "Long Answer", icon: FileText },
  cheat_sheet: { value: "cheat_sheet", label: "Cheat Sheet", icon: NotebookText },
};

// Groups the mode row by *kind*, not just alphabetically -- Explanation/Try
// Together are ways to learn, MCQ/Short/Long Answer are answer formats for
// self-testing, and Cheat Sheet is static reference material. Rendered flat
// they read as one undifferentiated row of pills; grouped with a caption per
// cluster, the different purposes are visible at a glance.
const MODE_GROUPS: { label: string; modes: ActiveMode[] }[] = [
  { label: "Learn", modes: ["explanation", "try_together"] },
  { label: "Practice", modes: ["mcq", "short_answer", "long_answer"] },
  { label: "Reference", modes: ["cheat_sheet"] },
];

// A garbled transcription of a table is easy to miss in a small plain-text
// box -- render it as an actual table so row/column mistakes are visible at
// a glance, instead of relying on the student to carefully read dense text.
function parseTablePreview(text: string): string[][] | null {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.includes("|") && !/^\|?\s*:?-{2,}/.test(l.replace(/\|/g, "").trim()));
  if (lines.length < 2) return null;
  return lines.map((l) => l.replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim()));
}

const LEVEL_OPTIONS: Record<Level, ModeOption<Level>> = {
  foundation: { value: "foundation", label: "Foundation", icon: Sprout },
  standard: { value: "standard", label: "Standard", icon: Target },
  advanced: { value: "advanced", label: "Advanced", icon: Zap },
  competitive: { value: "competitive", label: "Competitive", icon: Trophy },
};

const ALL_LEVELS: Level[] = ["foundation", "standard", "advanced", "competitive"];

interface ChatTurn {
  role: "user" | "assistant";
  content: string;
}

interface PublicQuestion {
  question: string;
  option_a?: string;
  option_b?: string;
  option_c?: string;
  option_d?: string;
}

// Each explain call returns distinct parts (structured output from Claude,
// not a single markdown blob) so they can be rendered as visually separate
// boxes instead of an undifferentiated wall of text.
interface ExplanationBlock {
  explanation: string;
  realLifeExample?: string;
  quickCheck?: string;
}

// Label-pill colors cycling per round -- the round itself is a neutral glass
// card; strong color differentiation happens at the content-type level
// (explanation vs real-life example vs quick check) instead.
const ROUND_LABEL_STYLES = [
  "bg-orange-100 text-coral-warm",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-sky-100 text-sky-700",
  "bg-rose-100 text-rose-700",
];

function renderExplanationBlock(block: ExplanationBlock, i: number) {
  return (
    <div key={i} className="rounded-2xl border border-gray-200 bg-white/70 backdrop-blur-md shadow-lg p-5 mb-4">
      <div className="flex items-center justify-between mb-3">
        <span className={`text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${ROUND_LABEL_STYLES[i % ROUND_LABEL_STYLES.length]}`}>
          {i === 0 ? "Explanation" : `Follow-up ${i}`}
        </span>
        <TTSButton text={block.explanation} />
      </div>
      <MarkdownExplanation text={block.explanation} variant="light" className="text-sm text-gray-800 mb-3" />
      {block.realLifeExample && (
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 mb-3 shadow-md backdrop-blur-sm">
          <p className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 uppercase tracking-wide mb-1.5">
            <Globe className="w-3.5 h-3.5" /> Real-life example
          </p>
          <MarkdownExplanation text={block.realLifeExample} variant="light" className="text-sm text-emerald-900" />
        </div>
      )}
      {block.quickCheck && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 shadow-md backdrop-blur-sm">
          <p className="flex items-center gap-1.5 text-xs font-bold text-amber-700 uppercase tracking-wide mb-1.5">
            <HelpCircle className="w-3.5 h-3.5" /> Quick check
          </p>
          <MarkdownExplanation text={block.quickCheck} variant="light" className="text-sm text-amber-900" />
        </div>
      )}
    </div>
  );
}

// Minimal shape used for the topic-search step -- StudentConceptState (real
// mastery history) satisfies this structurally, and concepts the student has
// never touched (no mastery_events at all, so absent from
// get_student_concept_state's result entirely) get synthesized into the
// same shape with concept_state='not_started' -- see allQueueRows below.
interface QueueRow {
  concept_id: number;
  concept_name: string;
  concept_state: StudentConceptState["concept_state"];
  mastery_score: number;
  subject_name: string;
}

function softTag(state: QueueRow["concept_state"]): string | null {
  switch (state) {
    case "building":
      return "could use another look";
    case "exploring":
      return "worth reviewing";
    case "not_started":
      return "haven't tried yet";
    default:
      return null;
  }
}

// An entry the student has actively added to their queue: either a real
// catalog concept, or free text typed/uploaded/pasted for something not in
// the catalog (routed to the guided-session engine's existing raw-input
// path, same mechanism Phase 3's upload/paste flow uses -- no concept_id).
// `rawText` carries the full uploaded/pasted material when present; `name`
// is always the short display label (falls back to being the raw input
// itself for a plain typed topic name).
type SelectionEntry =
  | { type: "concept"; concept_id: number; concept_name: string }
  | { type: "custom"; name: string; rawText?: string };
type QueueEntry = { type: "concept"; concept: ConceptPlus1 } | { type: "custom"; name: string; rawText?: string };

function entryName(entry: QueueEntry): string {
  return entry.type === "concept" ? entry.concept.name : entry.name;
}

// Small edit-distance check so a typo (e.g. "phtosynthesis") can still
// surface the real catalog concept instead of silently becoming a separate
// custom/raw-input entry -- a lightweight stand-in for real spell-check.
function levenshtein(a: string, b: string): number {
  const dp: number[][] = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[a.length][b.length];
}

function findClosestMatch(query: string, rows: QueueRow[]): QueueRow | null {
  const q = query.trim().toLowerCase();
  if (q.length < 3) return null;
  let best: { row: QueueRow; dist: number } | null = null;
  for (const row of rows) {
    const dist = levenshtein(q, row.concept_name.toLowerCase());
    if (dist <= Math.max(2, Math.floor(row.concept_name.length / 4)) && (!best || dist < best.dist)) {
      best = { row, dist };
    }
  }
  return best?.row ?? null;
}

// Matches free-text hub input against the tracked sub-topics from this
// topic's own explanation(s) -- exact/substring first (e.g. typing "Soil
// resources" verbatim), then a typo-tolerant fallback. This is what lets a
// student type a sibling topic's name instead of clicking its chip and
// still get routed to Explanation mode for it, instead of the free text
// being silently absorbed into whatever mode is currently active.
function findClosestTopic(query: string, topics: string[]): string | null {
  const q = query.trim().toLowerCase();
  if (q.length < 3) return null;
  const exact = topics.find((t) => {
    const tl = t.toLowerCase();
    return tl === q || tl.includes(q) || q.includes(tl);
  });
  if (exact) return exact;
  let best: { topic: string; dist: number } | null = null;
  for (const topic of topics) {
    const dist = levenshtein(q, topic.toLowerCase());
    if (dist <= Math.max(2, Math.floor(topic.length / 4)) && (!best || dist < best.dist)) {
      best = { topic, dist };
    }
  }
  return best?.topic ?? null;
}

export default function TestPrepSessionPage() {
  const { session, isLoading } = useAuth();

  const [phase, setPhase] = useState<Phase>("loading");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Topic picking (search across the whole grade catalog, no subject gate)
  const [conceptStates, setConceptStates] = useState<StudentConceptState[]>([]);
  const [allConcepts, setAllConcepts] = useState<ConceptPlus1[]>([]);
  const [selectedEntries, setSelectedEntries] = useState<SelectionEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadMode, setUploadMode] = useState<"image" | "paste" | "confirm" | null>(null);
  const [pasteDraft, setPasteDraft] = useState("");
  const [pendingRawText, setPendingRawText] = useState("");
  const pendingTablePreview = useMemo(() => parseTablePreview(pendingRawText), [pendingRawText]);

  // Queue
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [queueIndex, setQueueIndex] = useState(0);

  // Session state (scoped to the current queue item)
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [level, setLevel] = useState<Level>("advanced");
  const [depth, setDepth] = useState<Depth>("explain_properly");
  const [explanationBlocks, setExplanationBlocks] = useState<ExplanationBlock[]>([]);
  const [subTopics, setSubTopics] = useState<string[]>([]);

  // Hub
  const [activeMode, setActiveMode] = useState<ActiveMode>("explanation");
  const [hubInput, setHubInput] = useState("");
  const [sessionNote, setSessionNote] = useState("");

  // Try Together
  const [chatTurns, setChatTurns] = useState<ChatTurn[]>([]);

  // Practice
  const [practiceMode, setPracticeMode] = useState<PracticeMode | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<PublicQuestion | null>(null);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [answerText, setAnswerText] = useState("");
  const [evalResult, setEvalResult] = useState<{ feedback: string; wasStrong: boolean } | null>(null);

  // Cheat Sheet
  const [cheatSheet, setCheatSheet] = useState<{ status: "idle" | "loading" | "ready" | "error"; items?: { label: string; value: string }[] }>({
    status: "idle",
  });

  useEffect(() => {
    if (!session?.student_id) return;
    let mounted = true;
    Promise.all([
      getStudentConceptState(session.student_id),
      // Full concepts_plus1 catalog (not just subjects the student has
      // already touched) so search can find anything the board offers --
      // including concepts with zero mastery history yet.
      fetchConceptsPlus1(),
    ]).then(([states, concepts]) => {
      if (!mounted) return;
      setConceptStates(states.filter((s) => s.subject_name && s.concept_name));
      setAllConcepts(concepts);
      setPhase("pick_topics");
    });
    return () => {
      mounted = false;
    };
  }, [session?.student_id]);

  // Combines real mastery history with the rest of the grade's whole
  // catalog (every subject at once, not gated behind picking one first) --
  // concepts the student has never touched at all (zero mastery_events, so
  // absent from get_student_concept_state's result) still need to be
  // searchable so a never-studied subject isn't a dead end.
  const allQueueRows: QueueRow[] = useMemo(() => {
    const touched: QueueRow[] = conceptStates.map((s) => ({
      concept_id: s.concept_id,
      concept_name: s.concept_name,
      concept_state: s.concept_state,
      mastery_score: s.mastery_score,
      subject_name: s.subject_name,
    }));
    const touchedIds = new Set(touched.map((s) => s.concept_id));
    const untouched: QueueRow[] = allConcepts
      .filter((c) => !touchedIds.has(c.id))
      .map((c) => ({
        concept_id: c.id,
        concept_name: c.name,
        concept_state: "not_started" as const,
        mastery_score: 0,
        subject_name: c.subject_name ?? "",
      }));
    return [...touched, ...untouched];
  }, [conceptStates, allConcepts]);

  const selectedConceptIds = useMemo(
    () => new Set(selectedEntries.filter((e) => e.type === "concept").map((e) => e.concept_id)),
    [selectedEntries]
  );

  const searchMatches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return allQueueRows.filter((row) => !selectedConceptIds.has(row.concept_id) && row.concept_name.toLowerCase().includes(q)).slice(0, 8);
  }, [searchQuery, allQueueRows, selectedConceptIds]);

  // A typo-tolerant fallback (small edit-distance check, not a real spell
  // checker) so e.g. "phtosynthesis" still surfaces the real catalog concept
  // instead of silently becoming a separate custom/raw-input entry.
  const spellSuggestion = useMemo(() => {
    if (!searchQuery.trim() || searchMatches.length > 0) return null;
    return findClosestMatch(searchQuery, allQueueRows.filter((r) => !selectedConceptIds.has(r.concept_id)));
  }, [searchQuery, searchMatches, allQueueRows, selectedConceptIds]);

  const currentEntry = queue[queueIndex];

  function entryContext(entry: QueueEntry, useLevel: Level): GuidedConceptContext {
    if (entry.type === "concept") {
      const concept = entry.concept;
      const subject = concept.subject_name ?? "General";
      return {
        concept_name: concept.name,
        subject,
        grade_level: session?.grade_level ?? 11,
        explanation_basic: concept.explanation_basic ?? undefined,
        explanation_standard: concept.explanation_standard ?? undefined,
        explanation_advanced: concept.explanation_advanced ?? undefined,
        real_life_example: concept.real_life_example ?? undefined,
        common_mistakes: concept.common_mistakes ?? undefined,
        competitive_context: useLevel === "competitive" ? competitiveContextForSubject(subject) : undefined,
      };
    }
    return {
      concept_name: entry.name,
      subject: "General",
      grade_level: session?.grade_level ?? 11,
      raw_input_text: entry.rawText ?? entry.name,
    };
  }

  function resetPerConceptState() {
    setSessionId(null);
    setExplanationBlocks([]);
    setSubTopics([]);
    setActiveMode("explanation");
    setHubInput("");
    setSessionNote("");
    setChatTurns([]);
    setPracticeMode(null);
    setCurrentQuestion(null);
    setSelectedOption(null);
    setAnswerText("");
    setEvalResult(null);
    setCheatSheet({ status: "idle" });
  }

  function addConceptEntry(row: QueueRow) {
    setSelectedEntries((prev) =>
      prev.some((e) => e.type === "concept" && e.concept_id === row.concept_id)
        ? prev
        : [...prev, { type: "concept", concept_id: row.concept_id, concept_name: row.concept_name }]
    );
    setSearchQuery("");
  }

  function addCustomEntry(name: string, rawText?: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSelectedEntries((prev) => [...prev, { type: "custom", name: trimmed, rawText: rawText?.trim() || undefined }]);
    setSearchQuery("");
  }

  function removeEntry(index: number) {
    setSelectedEntries((prev) => prev.filter((_, i) => i !== index));
  }

  // Upload/paste, mirroring UnderstandSessionPage.tsx's Phase 3 flow exactly
  // (same decode-upload edge function, same no-persistence guarantee) but as
  // an "add to the list" action here instead of the whole page's one input.
  async function handleImageSelected(file: File) {
    setLoading(true);
    setErrorMsg(null);
    try {
      const { base64, mediaType } = await resizeImageFileForUpload(file);
      const result = await decodeUploadedImage({ imageBase64: base64, mediaType, gradeLevel: session?.grade_level });
      if (result.error || !result.text) throw new Error(result.error ?? "Could not read that photo — try a clearer one.");
      setPendingRawText(result.text);
      setUploadMode("confirm");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong — try again.");
    } finally {
      setLoading(false);
    }
  }

  function handlePasteContinue() {
    if (!pasteDraft.trim()) return;
    setPendingRawText(pasteDraft.trim());
    setUploadMode("confirm");
  }

  function handleConfirmUpload() {
    if (!pendingRawText.trim()) return;
    const words = pendingRawText.trim().split(/\s+/);
    const shortLabel = words.slice(0, 6).join(" ") + (words.length > 6 ? "…" : "");
    addCustomEntry(shortLabel, pendingRawText.trim());
    setUploadMode(null);
    setPendingRawText("");
    setPasteDraft("");
  }

  async function startConceptSession(entry: QueueEntry, useLevel: Level, useDepth: Depth) {
    if (!session?.student_id) return;
    setPhase("session_hub");
    setActiveMode("explanation");
    const start = await startGuidedSession(
      entry.type === "concept"
        ? { studentId: session.student_id, conceptId: entry.concept.id, conceptTable: "concepts_plus1", depth: useDepth, level: useLevel }
        : { studentId: session.student_id, depth: useDepth, level: useLevel, sourceType: "paste", rawInputText: entry.name }
    );
    if (start.error) throw new Error(start.error);
    setSessionId(start.session_id);

    const explained = await explainAtDepth(start.session_id, entryContext(entry, useLevel), useLevel, useDepth);
    if (explained.error) throw new Error(explained.error);
    setExplanationBlocks([
      { explanation: explained.explanation, realLifeExample: explained.real_life_example, quickCheck: explained.quick_check },
    ]);
    setSubTopics(explained.sub_topics ?? []);
  }

  async function handleConfirmConcepts() {
    if (selectedEntries.length === 0 || !session) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const conceptIds = selectedEntries.filter((e) => e.type === "concept").map((e) => e.concept_id);
      const fetched = conceptIds.length > 0 ? await fetchConceptsPlus1ByIds(conceptIds) : [];
      const byId = new Map(fetched.map((c) => [c.id, c]));
      // fetchConceptsPlus1ByIds doesn't select subject_name (not a real column
      // on concepts_plus1 -- only available via the get_concepts_plus1 RPC
      // join). Without it, subjectSupportsCompetitive() silently fails and
      // the Competitive tab never appears, even for Math/Physics/Chemistry/
      // Biology concepts. Backfill from allQueueRows, which already carries
      // subject_name for every concept shown in search.
      const subjectNameByConceptId = new Map(allQueueRows.map((r) => [r.concept_id, r.subject_name]));
      const ordered: QueueEntry[] = [];
      for (const e of selectedEntries) {
        if (e.type === "custom") {
          ordered.push({ type: "custom", name: e.name, rawText: e.rawText });
          continue;
        }
        const concept = byId.get(e.concept_id);
        if (concept) {
          ordered.push({
            type: "concept",
            concept: { ...concept, subject_name: concept.subject_name ?? subjectNameByConceptId.get(concept.id) },
          });
        }
      }
      setQueue(ordered);
      setQueueIndex(0);
      const initialLevel = defaultLevelForGrade(session.grade_level);
      const initialDepth: Depth = "explain_properly";
      setLevel(initialLevel);
      setDepth(initialDepth);
      await startConceptSession(ordered[0], initialLevel, initialDepth);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong — try again.");
    } finally {
      setLoading(false);
    }
  }

  // Explicit, deliberate request to explain a specific topic (a chip click,
  // or hub-input text that matched a tracked sub-topic) -- always re-explains
  // via `explain`, regardless of entry type, and always switches to
  // Explanation mode. This is the fix for topics becoming unreachable once
  // a different mode was entered.
  async function explainWithFocus(topic: string) {
    if (!sessionId) return;
    const entry = queue[queueIndex];
    setLoading(true);
    setErrorMsg(null);
    try {
      let context: GuidedConceptContext;
      if (entry.type === "custom") {
        const rawBase = entry.rawText ?? entry.name;
        const narrowedRaw = `${rawBase}\n\nWhat the student wants to focus on: ${topic}`;
        context = { concept_name: entry.name, subject: "General", grade_level: session?.grade_level ?? 11, raw_input_text: narrowedRaw };
        setQueue((prev) => prev.map((e, i) => (i === queueIndex && e.type === "custom" ? { ...e, rawText: narrowedRaw } : e)));
      } else {
        context = entryContext(entry, level);
      }
      const explained = await explainAtDepth(sessionId, context, level, depth, topic);
      if (explained.error) throw new Error(explained.error);
      setExplanationBlocks((prev) => [
        ...prev,
        { explanation: explained.explanation, realLifeExample: explained.real_life_example, quickCheck: explained.quick_check },
      ]);
      if (explained.sub_topics && explained.sub_topics.length > 0) {
        setSubTopics((prev) => Array.from(new Set([...prev, ...explained.sub_topics!])));
      }
      setActiveMode("explanation");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong — try again.");
    } finally {
      setLoading(false);
    }
  }

  // Switching level or depth is a fresh restart of the explanation at the new
  // rigor/style -- not an incremental follow-up like explainWithFocus -- so
  // accumulated mode content is cleared first.
  async function reexplainAtSettings(useLevel: Level, useDepth: Depth) {
    if (!sessionId) return;
    const entry = queue[queueIndex];
    setExplanationBlocks([]);
    setSubTopics([]);
    setChatTurns([]);
    setPracticeMode(null);
    setCurrentQuestion(null);
    setSelectedOption(null);
    setAnswerText("");
    setEvalResult(null);
    setCheatSheet({ status: "idle" });
    setSessionNote("");
    setActiveMode("explanation");
    setLoading(true);
    setErrorMsg(null);
    try {
      const explained = await explainAtDepth(sessionId, entryContext(entry, useLevel), useLevel, useDepth);
      if (explained.error) throw new Error(explained.error);
      setExplanationBlocks([
        { explanation: explained.explanation, realLifeExample: explained.real_life_example, quickCheck: explained.quick_check },
      ]);
      setSubTopics(explained.sub_topics ?? []);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong — try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleSwitchLevel(newLevel: Level) {
    if (newLevel === level) return;
    setLevel(newLevel);
    reexplainAtSettings(newLevel, depth);
  }

  function handleSwitchDepth(newDepth: Depth) {
    if (newDepth === depth) return;
    setDepth(newDepth);
    if (newDepth === "lets_just_solve") {
      setExplanationBlocks([]);
      setSubTopics([]);
      setActiveMode("try_together");
      setChatTurns([]);
      startGuidedPracticeKickoff(newDepth);
    } else {
      reexplainAtSettings(level, newDepth);
    }
  }

  async function startGuidedPracticeKickoff(useDepth: Depth = depth) {
    if (!sessionId) return;
    const entry = queue[queueIndex];
    setLoading(true);
    setErrorMsg(null);
    setSessionNote("");
    try {
      const result = await sendGuidedPracticeTurn(sessionId, entryContext(entry, level), level, useDepth);
      if (result.error) throw new Error(result.error);
      setChatTurns([{ role: "assistant", content: result.reply }]);
      if (result.done) setSessionNote(result.reply);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong — try again.");
    } finally {
      setLoading(false);
    }
  }

  async function sendGuidedPracticeMessage(message: string) {
    if (!sessionId) return;
    const entry = queue[queueIndex];
    setChatTurns((prev) => [...prev, { role: "user", content: message }]);
    setLoading(true);
    setErrorMsg(null);
    try {
      const result = await sendGuidedPracticeTurn(sessionId, entryContext(entry, level), level, depth, message);
      if (result.error) throw new Error(result.error);
      setChatTurns((prev) => [...prev, { role: "assistant", content: result.reply }]);
      if (result.done) setSessionNote(result.reply);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong — try again.");
    } finally {
      setLoading(false);
    }
  }

  // Persistent hub input: first checks whether the student typed something
  // that matches one of the topics already on offer (from any explanation
  // so far, not just the current one) -- if so, that takes priority over
  // whatever mode happens to be active, since a named topic is unambiguous.
  // Otherwise falls through to the current mode's normal handler.
  function handleHubInputSubmit() {
    const message = hubInput.trim();
    if (!message) return;
    setHubInput("");
    const match = findClosestTopic(message, subTopics);
    if (match) {
      explainWithFocus(match);
      return;
    }
    if (activeMode === "try_together") {
      sendGuidedPracticeMessage(message);
    } else {
      explainWithFocus(message);
    }
  }

  async function handleChooseMode(mode: PracticeMode) {
    if (!sessionId) return;
    const entry = queue[queueIndex];
    setLoading(true);
    setErrorMsg(null);
    setSelectedOption(null);
    setAnswerText("");
    setEvalResult(null);
    setSessionNote("");
    try {
      const result = await choosePracticeMode(sessionId, entryContext(entry, level), mode, level, depth);
      if (result.error) throw new Error(result.error);
      setPracticeMode(mode);
      if (result.max_reached) {
        setCurrentQuestion(null);
        setSessionNote("You've done a lot of practice on this topic — nice work! Wrap up whenever you're ready, or explore something else from the panel above.");
        return;
      }
      setCurrentQuestion({
        question: result.question ?? "",
        option_a: result.option_a,
        option_b: result.option_b,
        option_c: result.option_c,
        option_d: result.option_d,
      });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong — try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleSwitchMode(mode: ActiveMode) {
    setActiveMode(mode);
    if (mode === "try_together" && chatTurns.length === 0) {
      startGuidedPracticeKickoff();
    } else if ((mode === "mcq" || mode === "short_answer" || mode === "long_answer") && (!currentQuestion || practiceMode !== mode)) {
      handleChooseMode(mode);
    } else if (mode === "cheat_sheet" && cheatSheet.status === "idle") {
      loadCheatSheet();
    }
  }

  async function loadCheatSheet() {
    const entry = queue[queueIndex];
    if (!session || entry.type !== "concept") return;
    setCheatSheet({ status: "loading" });
    const concept = entry.concept;
    const result = await generateExamPrepContent({
      conceptId: concept.id,
      conceptName: concept.name,
      subject: concept.subject_name ?? "General",
      gradeBand: gradeBandFor(session.grade_level),
      contentType: "cheat_sheet",
      conceptTable: "concepts_plus1",
    });
    if (result.error || !result.content_json) {
      setCheatSheet({ status: "error" });
      return;
    }
    const content = result.content_json as CheatSheetContent;
    setCheatSheet({ status: "ready", items: content.items });
  }

  async function handleSubmitAnswer() {
    if (!sessionId) return;
    const entry = queue[queueIndex];
    setLoading(true);
    setErrorMsg(null);
    try {
      const result = await evaluatePracticeAnswer({
        sessionId,
        concept: entryContext(entry, level),
        optionChosen: practiceMode === "mcq" ? selectedOption ?? undefined : undefined,
        answerText: practiceMode !== "mcq" ? answerText : undefined,
        level,
        depth,
      });
      if (result.error) throw new Error(result.error);
      setEvalResult({ feedback: result.feedback, wasStrong: result.was_strong });
      if (result.should_conclude) {
        setSessionNote("You're doing really well here — whenever you're ready, you can wrap up this topic using the button below.");
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong — try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleConclude() {
    if (!sessionId || !session) return;
    const entry = queue[queueIndex];
    setLoading(true);
    setErrorMsg(null);
    setPhase("concluding");
    try {
      const result = await concludeGuidedSession(sessionId, entryContext(entry, level), level, depth);
      if (result.error) throw new Error(result.error);

      const nextIndex = queueIndex + 1;
      if (nextIndex < queue.length) {
        resetPerConceptState();
        setQueueIndex(nextIndex);
        const nextLevel = defaultLevelForGrade(session.grade_level);
        const nextDepth: Depth = "explain_properly";
        setLevel(nextLevel);
        setDepth(nextDepth);
        await startConceptSession(queue[nextIndex], nextLevel, nextDepth);
      } else {
        setPhase("queue_complete");
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong — try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleStartOver() {
    setPhase("pick_topics");
    setSelectedEntries([]);
    setSearchQuery("");
    setUploadMode(null);
    setPasteDraft("");
    setPendingRawText("");
    setQueue([]);
    setQueueIndex(0);
    resetPerConceptState();
    setErrorMsg(null);
  }

  const inRun = phase === "session_hub" || phase === "concluding";
  const availableModes: ActiveMode[] = [
    "explanation",
    "try_together",
    "mcq",
    "short_answer",
    "long_answer",
    ...(currentEntry?.type === "concept" ? (["cheat_sheet"] as ActiveMode[]) : []),
  ];
  const currentSubjectName = currentEntry?.type === "concept" ? currentEntry.concept.subject_name ?? undefined : undefined;
  const availableLevels = ALL_LEVELS.filter((l) => l !== "competitive" || subjectSupportsCompetitive(currentSubjectName));
  const hubInputVisible = activeMode === "explanation" || activeMode === "try_together";
  const hubInputPlaceholder =
    activeMode === "try_together" ? "What do you think comes next?" : "Ask something or type a topic name…";

  if (isLoading || !session) {
    return <div className="max-w-2xl mx-auto p-6 pt-24 text-gray-400">Loading…</div>;
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 pt-24">
      <TopTearCard
        icon={<NotebookPen size={20} />}
        title="Prepare for a Test"
        subtitle="Add topics, then we'll work through them one by one"
        tone="marigold"
        spill={<SpillingDoodlesCompact />}
      >
      <div className="max-w-3xl mx-auto">

      {errorMsg && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">{errorMsg}</div>
      )}

      {inRun && queue.length > 0 && (
        <p className="text-xs text-gray-400 mb-3">Concept {queueIndex + 1} of {queue.length}</p>
      )}

      {phase === "loading" && <p className="text-gray-400">Loading…</p>}

      {phase === "pick_topics" && (
        <div>
          <div className="relative mb-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search concepts… e.g. Thermodynamics, Integration"
              className="w-full p-3 rounded-lg border border-gray-300 text-gray-900"
            />
            {searchQuery.trim() && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                {searchMatches.map((row) => {
                  const tag = softTag(row.concept_state);
                  return (
                    <button
                      key={row.concept_id}
                      onClick={() => addConceptEntry(row)}
                      className="w-full text-left px-3 py-2 hover:bg-orange-50 flex items-center justify-between gap-2"
                    >
                      <span>
                        <span className="text-gray-900">{row.concept_name}</span>
                        {row.subject_name && <span className="text-gray-400 text-xs ml-2">{row.subject_name}</span>}
                      </span>
                      {tag && <span className="text-xs text-gray-400 flex-shrink-0">{tag}</span>}
                    </button>
                  );
                })}
                {spellSuggestion && (
                  <button
                    onClick={() => addConceptEntry(spellSuggestion)}
                    className="w-full text-left px-3 py-2 hover:bg-orange-50 text-gray-700 text-sm border-t border-gray-100"
                  >
                    Did you mean <span className="font-medium">{spellSuggestion.concept_name}</span>?
                  </button>
                )}
                <button
                  onClick={() => addCustomEntry(searchQuery)}
                  className="w-full text-left px-3 py-2 hover:bg-orange-50 text-coral-warm text-sm border-t border-gray-100"
                >
                  + Add "{searchQuery.trim()}" as a custom topic
                </button>
              </div>
            )}
          </div>

          {!uploadMode && (
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setUploadMode("image")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-coral/10 border-2 border-coral text-coral hover:bg-coral hover:text-white transition-all"
              >
                <Camera className="w-3.5 h-3.5" />
                Upload a photo
              </button>
              <button
                onClick={() => setUploadMode("paste")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-night/10 border-2 border-night text-night hover:bg-night hover:text-white transition-all"
              >
                <ClipboardPaste className="w-3.5 h-3.5" />
                Paste text instead
              </button>
            </div>
          )}

          {uploadMode === "image" && (
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-3 text-xs text-gray-600">
                <p className="font-medium text-gray-700 mb-1">For the clearest results:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>One page per photo (instead of two pages together)</li>
                  <li>Hold the phone directly above the page</li>
                  <li>Fill the frame with the page</li>
                  <li>Ensure good lighting and no perspective tilt</li>
                </ul>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => e.target.files?.[0] && handleImageSelected(e.target.files[0])}
                className="text-sm"
              />
              {loading && <p className="text-gray-400 text-sm mt-2">Reading photo…</p>}
              <div>
                <button onClick={() => setUploadMode(null)} className="text-xs text-gray-400 mt-2 hover:text-gray-600">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {uploadMode === "paste" && (
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <textarea
                value={pasteDraft}
                onChange={(e) => setPasteDraft(e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                placeholder="Paste your chapter or question here…"
              />
              <div className="flex gap-3 mt-2 items-center">
                <button
                  onClick={handlePasteContinue}
                  disabled={!pasteDraft.trim()}
                  className="bg-coral-warm text-white px-3 py-1.5 rounded-lg text-sm disabled:opacity-50"
                >
                  Continue
                </button>
                <button
                  onClick={() => {
                    setUploadMode(null);
                    setPasteDraft("");
                  }}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {uploadMode === "confirm" && (
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-xs text-gray-500 mb-2">Here's what we found — is this right? Feel free to edit it.</p>
              {pendingTablePreview && (
                <div className="overflow-x-auto mb-3 border border-gray-200 rounded-lg">
                  <p className="text-xs text-gray-500 px-2 pt-2">Looks like a table — check every row carefully, especially anything marked [unclear]:</p>
                  <table className="w-full text-xs border-collapse mt-2">
                    <tbody>
                      {pendingTablePreview.map((row, i) => (
                        <tr key={i} className={i === 0 ? "bg-gray-50 font-semibold" : ""}>
                          {row.map((cell, j) => (
                            <td
                              key={j}
                              className={`border border-gray-200 px-2 py-1 ${cell.toLowerCase().includes("unclear") ? "bg-yellow-50 text-yellow-700" : ""}`}
                            >
                              {cell || "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <textarea
                value={pendingRawText}
                onChange={(e) => setPendingRawText(e.target.value)}
                rows={pendingTablePreview ? 10 : 5}
                className="w-full border border-gray-300 rounded-lg p-2 text-sm"
              />
              <div className="flex gap-3 mt-2 items-center">
                <button
                  onClick={handleConfirmUpload}
                  disabled={!pendingRawText.trim()}
                  className="bg-coral-warm text-white px-3 py-1.5 rounded-lg text-sm disabled:opacity-50"
                >
                  Add to list
                </button>
                <button
                  onClick={() => {
                    setUploadMode(null);
                    setPendingRawText("");
                  }}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {selectedEntries.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5 mt-3">
              {selectedEntries.map((entry, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 bg-orange-50 border border-orange-200 text-orange-800 text-sm px-3 py-1.5 rounded-full"
                >
                  {entry.type === "concept" ? entry.concept_name : entry.name}
                  <button onClick={() => removeEntry(i)} className="text-orange-300 hover:text-orange-600">
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}

          <button
            onClick={handleConfirmConcepts}
            disabled={loading || selectedEntries.length === 0}
            className="inline-flex items-center gap-2 bg-coral text-white px-5 py-2.5 rounded-xl font-bold border-2 border-ink shadow-sticker hover:translate-y-0.5 hover:shadow-none transition-all disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-sticker"
          >
            Start with {selectedEntries.length} {selectedEntries.length === 1 ? "topic" : "topics"}
            <ArrowRight className="w-4 h-4" />
          </button>
          {loading && <p className="text-gray-400 text-sm mt-2">Getting things ready…</p>}
        </div>
      )}

      {phase === "session_hub" && (
        <div>
          <p className="text-lg font-medium text-gray-900 mb-3">{currentEntry ? entryName(currentEntry) : ""}</p>

          <div className="mb-3">
            <ModeSelector
              options={availableLevels.map((lvl) => LEVEL_OPTIONS[lvl])}
              active={level}
              onChange={handleSwitchLevel}
              disabled={loading}
            />
          </div>

          <div className="mb-5">
            <ModeSelector
              groups={MODE_GROUPS.map((g) => ({
                label: g.label,
                options: g.modes.filter((m) => availableModes.includes(m)).map((m) => MODE_OPTIONS[m]),
              })).filter((g) => g.options.length > 0)}
              active={activeMode}
              onChange={handleSwitchMode}
            />
          </div>

          {activeMode === "explanation" && (
            <div>
              {explanationBlocks.length === 0 ? (
                <p className="text-gray-400">Thinking…</p>
              ) : (
                <>
                  {explanationBlocks.map((block, i) => renderExplanationBlock(block, i))}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <button
                      onClick={() => handleSwitchDepth("quick_recap")}
                      disabled={loading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    >
                      <Repeat className="w-3.5 h-3.5" />
                      Just a quick recap
                    </button>
                    <button
                      onClick={() => handleSwitchDepth("lets_just_solve")}
                      disabled={loading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    >
                      <Calculator className="w-3.5 h-3.5" />
                      Skip to practice
                    </button>
                  </div>
                  {subTopics.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Jump to a part</p>
                      <div className="flex flex-wrap gap-2">
                        {subTopics.map((topic, i) => (
                          <button
                            key={i}
                            onClick={() => explainWithFocus(topic)}
                            disabled={loading}
                            className="px-3 py-1.5 rounded-full text-sm border border-orange-200 bg-orange-50 text-coral-warm hover:bg-orange-100 disabled:opacity-50"
                          >
                            {topic}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeMode === "try_together" && (
            <div>
              <div className="space-y-3 mb-4">
                {chatTurns.map((t, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-lg text-sm ${
                      t.role === "assistant" ? "bg-orange-50 text-ocean-deep" : "bg-gray-100 text-gray-800 ml-8"
                    }`}
                  >
                    {t.role === "assistant" ? (
                      <>
                        <div className="flex justify-end mb-1">
                          <TTSButton text={t.content} />
                        </div>
                        <MarkdownExplanation text={t.content} variant="light" />
                      </>
                    ) : (
                      <span className="whitespace-pre-wrap">{t.content}</span>
                    )}
                  </div>
                ))}
                {loading && <p className="text-gray-400 text-sm">Thinking…</p>}
              </div>
            </div>
          )}

          {(activeMode === "mcq" || activeMode === "short_answer" || activeMode === "long_answer") && (
            <div>
              {!currentQuestion ? (
                <p className="text-gray-400">{loading ? "Thinking…" : sessionNote || "Getting a question ready…"}</p>
              ) : (
                <>
                  <div className="flex items-start gap-2 mb-4">
                    <MarkdownExplanation text={currentQuestion.question} variant="light" className="font-medium text-gray-900 flex-1" />
                    <TTSButton text={currentQuestion.question} />
                  </div>

                  {practiceMode === "mcq" ? (
                    <div className="grid gap-2 mb-4">
                      {(["option_a", "option_b", "option_c", "option_d"] as const).map((key, idx) => (
                        <button
                          key={key}
                          disabled={!!evalResult}
                          onClick={() => setSelectedOption(idx)}
                          className={`text-left p-3 rounded-lg border ${
                            selectedOption === idx ? "border-coral-warm bg-orange-50" : "border-gray-200"
                          } disabled:opacity-70`}
                        >
                          {currentQuestion[key]}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <textarea
                      value={answerText}
                      onChange={(e) => setAnswerText(e.target.value)}
                      disabled={!!evalResult}
                      rows={4}
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm mb-4"
                      placeholder="Write your answer…"
                    />
                  )}

                  {evalResult ? (
                    <div
                      className={`rounded-2xl p-5 mb-4 border-2 ${
                        evalResult.wasStrong
                          ? "bg-white border-ink shadow-sticker"
                          : "bg-marigold/10 border-ink shadow-sticker"
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        {evalResult.wasStrong ? (
                          <PartyPopper className="w-9 h-9 text-coral" />
                        ) : (
                          <Lightbulb className="w-9 h-9 text-marigold" />
                        )}
                        <span className="text-xl font-extrabold text-ink">
                          {evalResult.wasStrong ? "Nice work!" : "Let's take another look"}
                        </span>
                      </div>
                      <MarkdownExplanation
                        text={evalResult.feedback}
                        variant="light"
                        className="text-sm text-ink"
                      />
                      {sessionNote && <p className="text-sm mt-3 text-gray-600">{sessionNote}</p>}
                    </div>
                  ) : null}

                  {!evalResult ? (
                    <>
                      <button
                        onClick={handleSubmitAnswer}
                        disabled={loading || (practiceMode === "mcq" ? selectedOption === null : !answerText.trim())}
                        className="bg-coral-warm text-white px-5 py-2.5 rounded-lg font-medium disabled:opacity-50"
                      >
                        Submit
                      </button>
                      {loading && <p className="text-gray-400 text-sm mt-2">Thinking…</p>}
                    </>
                  ) : (
                    <button
                      onClick={() => handleChooseMode(practiceMode!)}
                      className="bg-white border border-coral-warm text-coral-warm px-5 py-2.5 rounded-lg font-medium"
                    >
                      Try another
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {activeMode === "cheat_sheet" && (
            <div>
              {cheatSheet.status === "loading" && <p className="text-gray-400">Loading…</p>}
              {cheatSheet.status === "error" && <p className="text-red-500 text-sm">Couldn't load this — try again.</p>}
              {cheatSheet.status === "ready" && cheatSheet.items && (
                <div className="rounded-2xl border border-gray-200 bg-white/70 backdrop-blur-md shadow-lg p-5 space-y-2">
                  {cheatSheet.items.map((item, i) => (
                    <p key={i} className="text-sm text-gray-800">
                      <span className="font-semibold">{item.label}:</span> {item.value}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {hubInputVisible && (
            <div className="mt-2">
              {sessionNote && activeMode === "try_together" && (
                <div className="bg-orange-50 text-ocean-deep p-3 rounded-lg mb-3 text-sm">{sessionNote}</div>
              )}
              <div className="flex gap-2">
                <input
                  value={hubInput}
                  onChange={(e) => setHubInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !loading && handleHubInputSubmit()}
                  placeholder={hubInputPlaceholder}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  disabled={loading}
                />
                <button
                  onClick={handleHubInputSubmit}
                  disabled={loading || !hubInput.trim()}
                  className="bg-coral-warm text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {activeMode === "try_together" ? "Send" : "Ask"}
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-gray-100">
            <button onClick={handleConclude} disabled={loading} className="text-sm text-coral-warm hover:text-orange-600 font-medium disabled:opacity-50">
              ✓ Done with this topic →
            </button>
          </div>
        </div>
      )}

      {phase === "concluding" && <p className="text-gray-400">Wrapping up…</p>}

      {phase === "queue_complete" && (
        <div>
          <div className="bg-orange-50 text-ocean-deep p-4 rounded-lg mb-4">
            <p className="font-medium mb-2">You went through:</p>
            <ul className="list-disc list-inside text-sm space-y-1">
              {queue.map((entry, i) => (
                <li key={i}>{entryName(entry)}</li>
              ))}
            </ul>
            <p className="mt-3 text-sm">Good luck on your test!</p>
          </div>
          <div className="flex gap-3">
            <button onClick={handleStartOver} className="bg-coral-warm text-white px-5 py-2.5 rounded-lg font-medium">
              Prep more topics
            </button>
            <Link to="/dashboard" className="bg-white border border-coral-warm text-coral-warm px-5 py-2.5 rounded-lg font-medium">
              Back to Dashboard
            </Link>
          </div>
        </div>
      )}
      </div>
      </TopTearCard>
    </div>
  );
}
